/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectHotelName,
  selectVisualFilters,
  selectIsDemoUser,
  selectPmsSync,
  selectEnableHotelGroups,
  selectReportType,
  selectLocalCurrency,
  selectBreakdownDetails,
  // selectHotelGroupDropdownList,
  // selectHotelDropdownList,
} from 'modules/dashboard/selectors';
import {
  selectWebClickBreakdown,
  selectWebClickAlignmentToggle,
  selectWebClickCustomDate,
  selectWebClickOtb,
  selectWebClickAlignment,
  selectWebClickDateComparison,
  selectUtmFocusOn,
  selectUtmBreakdownSelection,
  selectWebClickBookingDate,
  selectWebClickTargetDate,
  selectWebClickPaceHorizon,
  selectWebClickSymmetricComparisonToggle,
  selectWebClickEndDate,
  selectWebClickStartDate,
  selectWebClickStep,
  selectWebClickHotelFocusOn,
  selectWebClickFocusOn,
  selectWebClickScalingFactor,
  selectWebClickNormalizedView,
  selectWebClickCumulativeView,
  selectWeeklyWebClickEndDate,
  selectWeeklyWebClickStartDate,
  selectWebClickWeeklyView,
  selectWebClickTimeVisualFilter,
  selectWebClickTimePaceVisualFilter,
  selectWebClickHotelVisualFilter,
  selectWebClickHotelPaceVisualFilter,
  selectWebClickSegmentVisualFilter,
  selectWebClickSegmentPaceVisualFilter,
  selectUtmFilterList,
  selectWebClickEnableSecondaryHotelGroups,
  selectTimeTrendIsDisplay,
  selectTimePaceIsDisplay,
  selectSegmentTrendIsDisplay,
  selectSegmentPaceIsDisplay,
  selectHotelTrendIsDisplay,
  selectHotelPaceIsDisplay,
  selectUtmTrendIsDisplay,
  selectUtmPaceIsDisplay,
} from 'modules/dashboard/components/tab-container/web-click-tab/selectors';
import { KEYS, REPORT_TYPE } from 'modules/dashboard/constants';
import {
  generateWebClicksShareUrl,
  getBreakdown,
  getFocusOnFilter,
  getAlignmentAndDateComparisonSchema,
  getBookingDateSchema,
  getChannelFocusOnSchema,
  getCustomDateComparisonSchema,
  getGroupASchema,
  getGroupHotelFilterSchema,
  getHotelGroupASchema,
  getHotelGroupSchema,
  getHotelIdSchema,
  getHotelNameOrderSchema,
  getNormalizedViewSchema,
  getPaceHorizonSchema,
  getScaleFactorSchema,
  getSymmetricComparisonSchema,
  getTargetDateSchema,
  getUtmBreakdownSchema,
  getUtmFocusOnSchema,
  getWebClicksStepsSchema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import { webClickActions } from 'modules/dashboard/components/tab-container/web-click-tab/slice';
import writeLog from 'modules/common/utils/filter-log';
import { ENVIRONMENT } from 'config';
import { selectSymmetricComparisonIsDisplay } from '../../summary-tab/selectors';

/**
 * Custom hook to generate web click view filter schemas
 */
const useWebClickView = () => {
  const dispatch = useDispatch();
  //
  const alignment = useSelector(selectWebClickAlignment);
  const dateComparison = useSelector(selectWebClickDateComparison);
  const bookingDate = useSelector(selectWebClickBookingDate);
  const targetDate = useSelector(selectWebClickTargetDate);
  const hotelId = useSelector(selectHotelName);
  const visualFilters = useSelector(selectVisualFilters);
  const paceHorizon = useSelector(selectWebClickPaceHorizon);
  const symmetricComparisonToggle = useSelector(selectWebClickSymmetricComparisonToggle);
  const endDate = useSelector(selectWebClickEndDate);
  const startDate = useSelector(selectWebClickStartDate);
  const focusOn = useSelector(selectWebClickFocusOn);
  const webClickStep = useSelector(selectWebClickStep);
  const scalingFactor = useSelector(selectWebClickScalingFactor);
  const normalizedViewToggle = useSelector(selectWebClickNormalizedView);
  const weeklyViewToggle = useSelector(selectWebClickWeeklyView);
  const targetStartDate = useSelector(selectWeeklyWebClickStartDate);
  const targetEndDate = useSelector(selectWeeklyWebClickEndDate);
  const isCumulative = useSelector(selectWebClickCumulativeView);
  const isDemoUser = useSelector(selectIsDemoUser);
  const vOTB = useSelector(selectWebClickOtb);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const alignmentToggle = useSelector(selectWebClickAlignmentToggle);
  const customDate = useSelector(selectWebClickCustomDate);
  const utmBreakdown = useSelector(selectUtmBreakdownSelection);
  const utmFocusOn = useSelector(selectUtmFocusOn);
  const hotelFocusOn = useSelector(selectWebClickHotelFocusOn);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const enableSecondaryHotelGroups = useSelector(selectWebClickEnableSecondaryHotelGroups);
  const reportType = useSelector(selectReportType);
  const timeVisualFilterList = useSelector(selectWebClickTimeVisualFilter);
  const timePaceVisualFilterList = useSelector(selectWebClickTimePaceVisualFilter);
  const hotelVisualFilterList = useSelector(selectWebClickHotelVisualFilter);
  const hotelPaceVisualFilterList = useSelector(selectWebClickHotelPaceVisualFilter);
  const segmentVisualFilterList = useSelector(selectWebClickSegmentVisualFilter);
  const segmentPaceVisualFilterList = useSelector(selectWebClickSegmentPaceVisualFilter);
  const utmVisualFilterList = useSelector(selectUtmFilterList);
  const compareWindowCollapseToggle = useSelector(selectSymmetricComparisonIsDisplay);
  const timeTrendIsDisplay = useSelector(selectTimeTrendIsDisplay);
  const timePaceIsDisplay = useSelector(selectTimePaceIsDisplay);
  const segmentTrendIsDisplay = useSelector(selectSegmentTrendIsDisplay);
  const segmentPaceIsDisplay = useSelector(selectSegmentPaceIsDisplay);
  const hotelTrendIsDisplay = useSelector(selectHotelTrendIsDisplay);
  const hotelPaceIsDisplay = useSelector(selectHotelPaceIsDisplay);
  const utmTrendIsDisplay = useSelector(selectUtmTrendIsDisplay);
  const utmPaceIsDisplay = useSelector(selectUtmPaceIsDisplay);
  const localCurrency = useSelector(selectLocalCurrency);
  const chartBreakdown = useSelector(selectWebClickBreakdown);
  const breakdownData = useSelector(selectBreakdownDetails);
  // focus on option list of selected breakdown
  const focusOnList = useMemo(
    () =>
      chartBreakdown
        ? getFocusOnFilter(breakdownData, getBreakdown(visualFilters, chartBreakdown))
        : [],
    [breakdownData, visualFilters, chartBreakdown]
  );
  //
  const [previousHotel, setPreviousHotel] = useState(null);
  //
  const serializedState = JSON.parse(localStorage.getItem(`persist:${ENVIRONMENT.PERSIST_KEY}`));
  const persistValues = Object.values(serializedState)[1];
  const persistedHotelName = JSON.parse(persistValues).hotelName;
  //
  useEffect(() => {
    // Persist hotel focus on value on browser refresh
    setPreviousHotel(persistedHotelName);
    if (previousHotel !== null) {
      // dispatch(webClickActions.setWebClickHotelFocusOn(hotelDropdownList[0]));
    }
  }, [hotelId]);

  // Generate filter list for web clicks visuals
  useEffect(() => {
    if (
      reportType !== REPORT_TYPE.WEB_CLICK ||
      (!dateComparison && !alignment && !bookingDate) ||
      (dateComparison && dateComparison === KEYS.CUSTOM && endDate === null)
    )
      return;
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    const alignmentAndDateComparisonFilter = getAlignmentAndDateComparisonSchema(
      visualFilters,
      alignment?.id,
      dateComparison
    );
    //
    let newStartDate;
    let newEndDate;
    if (isCumulative) {
      newStartDate = targetDate?.startDate;
      newEndDate = targetDate?.endDate;
    } else {
      newStartDate = weeklyViewToggle ? targetStartDate : targetDate?.startDate;
      newEndDate = weeklyViewToggle ? targetEndDate : targetDate?.endDate;
    }
    const targetDateFilter = getTargetDateSchema(visualFilters, newStartDate, newEndDate);
    //
    let hotelIdFilter = [];
    if (enableHotelGroups) {
      const hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode);
      hotelIdFilter = getHotelIdSchema(hotelListIds, visualFilters);
    } else {
      hotelIdFilter = getHotelIdSchema(hotelId?.hotelCode, visualFilters);
    }
    const paceHorizonFilter = getPaceHorizonSchema(visualFilters, paceHorizon);
    const symmetricComparisonFilter = getSymmetricComparisonSchema(
      visualFilters,
      symmetricComparisonToggle
    );
    const isCustomDateComparisonFilter = getCustomDateComparisonSchema(
      visualFilters,
      dateComparison,
      startDate,
      endDate
    );
    const webClickStepsFilter = getWebClicksStepsSchema(visualFilters, webClickStep);
    //
    if (
      bookingDateFilter &&
      alignmentAndDateComparisonFilter &&
      targetDateFilter &&
      hotelIdFilter &&
      paceHorizonFilter &&
      symmetricComparisonFilter &&
      webClickStepsFilter
    ) {
      const filters = [
        bookingDateFilter,
        ...alignmentAndDateComparisonFilter,
        targetDateFilter,
        hotelIdFilter,
        symmetricComparisonFilter,
        ...isCustomDateComparisonFilter,
        webClickStepsFilter,
        paceHorizonFilter,
      ];

      // trend time filter list
      if (JSON.stringify(filters) !== JSON.stringify(timeVisualFilterList)) {
        //
        writeLog('Web clicks Time Visual Filter', filters);
        dispatch(webClickActions.setWebClickTimeVisualFilter(filters));
      }
    }
    if (
      alignmentAndDateComparisonFilter &&
      targetDateFilter &&
      hotelIdFilter &&
      paceHorizonFilter &&
      webClickStepsFilter
    ) {
      const filters = [
        ...alignmentAndDateComparisonFilter,
        targetDateFilter,
        hotelIdFilter,
        ...isCustomDateComparisonFilter,
        webClickStepsFilter,
        paceHorizonFilter,
      ];

      // time pace filter list
      if (JSON.stringify(filters) !== JSON.stringify(timePaceVisualFilterList)) {
        //
        writeLog('Web clicks Time Pace Visual Filter', filters);
        dispatch(webClickActions.setWebClickTimePaceVisualFilter(filters));
      }
    }
  }, [
    bookingDate,
    visualFilters,
    targetDate,
    hotelId,
    paceHorizon,
    endDate,
    startDate,
    webClickStep,
    targetStartDate,
    targetEndDate,
    isCumulative,
    weeklyViewToggle,
    enableHotelGroups,
    reportType,
    alignment,
    symmetricComparisonToggle,
    dateComparison,
  ]);

  // Generate filter list for segment visuals
  useEffect(() => {
    if (reportType !== REPORT_TYPE.WEB_CLICK) return;
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    let newStartDate;
    let newEndDate;
    if (isCumulative) {
      newStartDate = targetDate?.startDate;
      newEndDate = targetDate?.endDate;
    } else {
      newStartDate = weeklyViewToggle ? targetStartDate : targetDate?.startDate;
      newEndDate = weeklyViewToggle ? targetEndDate : targetDate?.endDate;
    }
    const targetDateFilter = getTargetDateSchema(visualFilters, newStartDate, newEndDate);
    //
    let hotelIdFilter = [];
    if (enableHotelGroups) {
      const hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode);
      hotelIdFilter = getHotelIdSchema(hotelListIds, visualFilters);
    } else {
      hotelIdFilter = getHotelIdSchema(hotelId?.hotelCode, visualFilters);
    }
    const paceHorizonFilter = getPaceHorizonSchema(visualFilters, paceHorizon);
    const webClickStepsFilter = getWebClicksStepsSchema(visualFilters, webClickStep);
    const ChannelFocusOnFilter = getChannelFocusOnSchema(visualFilters, focusOn);
    const scaleFactorFilter = getScaleFactorSchema(visualFilters, scalingFactor);
    // trend segment filter list
    if (
      bookingDateFilter &&
      targetDateFilter &&
      hotelIdFilter &&
      webClickStepsFilter &&
      ChannelFocusOnFilter &&
      scaleFactorFilter
    ) {
      const filters = [
        bookingDateFilter,
        targetDateFilter,
        hotelIdFilter,
        webClickStepsFilter,
        ChannelFocusOnFilter,
        scaleFactorFilter,
      ];

      if (JSON.stringify(filters) !== JSON.stringify(segmentVisualFilterList)) {
        //
        writeLog('Web clicks Segment Visual Filter', filters);
        dispatch(webClickActions.setWebClickSegmentVisualFilter(filters));
      }
    }
    // segment pace filter list
    if (
      targetDateFilter &&
      hotelIdFilter &&
      paceHorizonFilter &&
      webClickStepsFilter &&
      ChannelFocusOnFilter &&
      scaleFactorFilter
    ) {
      const filters = [
        targetDateFilter,
        hotelIdFilter,
        webClickStepsFilter,
        paceHorizonFilter,
        ChannelFocusOnFilter,
        scaleFactorFilter,
      ];

      if (JSON.stringify(filters) !== JSON.stringify(segmentPaceVisualFilterList)) {
        //
        writeLog('Web Clicks Segment Pace Visual Filter', filters);
        dispatch(webClickActions.setWebClickSegmentPaceVisualFilter(filters));
      }
    }
  }, [
    bookingDate,
    visualFilters,
    targetDate,
    hotelId,
    paceHorizon,
    webClickStep,
    focusOn,
    scalingFactor,
    targetStartDate,
    targetEndDate,
    isCumulative,
    weeklyViewToggle,
    enableHotelGroups,
    reportType,
  ]);

  // Generate filter list for web clicks hotel visuals
  useEffect(() => {
    if (reportType !== REPORT_TYPE.WEB_CLICK) return;
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    let newStartDate;
    let newEndDate;
    if (isCumulative) {
      newStartDate = targetDate?.startDate;
      newEndDate = targetDate?.endDate;
    } else {
      newStartDate = weeklyViewToggle ? targetStartDate : targetDate?.startDate;
      newEndDate = weeklyViewToggle ? targetEndDate : targetDate?.endDate;
    }
    const targetDateFilter = getTargetDateSchema(visualFilters, newStartDate, newEndDate);
    const groupFocusOnIds = hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
    let hotelGroup1Filter;
    const hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
    //
    const groupHotel1Filter = getGroupHotelFilterSchema(visualFilters);
    if (enableHotelGroups) {
      hotelGroup1Filter = getHotelGroupSchema(hotelListIds, groupHotel1Filter);
    } else {
      hotelGroup1Filter = getHotelGroupSchema([hotelId?.hotelCode], groupHotel1Filter);
    }
    //
    const groupHotel2Filter = getGroupASchema(visualFilters);
    let hotelGroup2Filter;
    if (enableSecondaryHotelGroups) {
      hotelGroup2Filter = getHotelGroupASchema(groupFocusOnIds, groupHotel2Filter);
    } else {
      hotelGroup2Filter = getHotelGroupASchema([hotelFocusOn?.hotelCode], groupHotel2Filter);
    }
    //
    let dimHotelIds = [];
    if (enableHotelGroups) {
      if (enableSecondaryHotelGroups) {
        dimHotelIds = new Set([...groupFocusOnIds, ...hotelListIds]);
      } else {
        dimHotelIds = new Set([hotelFocusOn?.hotelCode, ...hotelListIds]);
      }
    } else if (enableSecondaryHotelGroups) {
      dimHotelIds = new Set([...groupFocusOnIds, hotelId?.hotelCode]);
    } else {
      dimHotelIds = new Set([hotelFocusOn?.hotelCode, hotelId?.hotelCode]);
    }
    //
    const secondaryHotelIdFilter = getHotelIdSchema(Array.from(dimHotelIds), visualFilters);
    const hotelFilters = [secondaryHotelIdFilter, hotelGroup2Filter, hotelGroup1Filter];
    const hotelNameOrderFilter = getHotelNameOrderSchema(visualFilters, isDemoUser);
    const paceHorizonFilter = getPaceHorizonSchema(visualFilters, paceHorizon);
    const webClickStepsFilter = getWebClicksStepsSchema(visualFilters, webClickStep);
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    // trend hotel filter list
    if (
      bookingDateFilter &&
      targetDateFilter &&
      hotelFilters &&
      webClickStepsFilter &&
      normalizedFilter &&
      hotelNameOrderFilter
    ) {
      const filters = [
        bookingDateFilter,
        targetDateFilter,
        ...hotelFilters,
        webClickStepsFilter,
        normalizedFilter,
        hotelNameOrderFilter,
      ];

      if (JSON.stringify(filters) !== JSON.stringify(hotelVisualFilterList)) {
        //
        writeLog('Web Click Hotel Visual Filter', filters);
        dispatch(webClickActions.setWebClickHotelVisualFilter(filters));
      }
    }
    // hotel pace filter list
    if (
      targetDateFilter &&
      hotelFilters &&
      paceHorizonFilter &&
      webClickStepsFilter &&
      hotelNameOrderFilter &&
      normalizedFilter
    ) {
      const filters = [
        targetDateFilter,
        ...hotelFilters,
        webClickStepsFilter,
        hotelNameOrderFilter,
        paceHorizonFilter,
        normalizedFilter,
      ];

      if (JSON.stringify(filters) !== JSON.stringify(hotelPaceVisualFilterList)) {
        //
        writeLog('Web Click Hotel Pace Visual Filter', filters);
        dispatch(webClickActions.setWebClickHotelPaceVisualFilter(filters));
      }
    }
  }, [
    bookingDate,
    visualFilters,
    targetDate,
    hotelId,
    paceHorizon,
    webClickStep,
    normalizedViewToggle,
    hotelFocusOn,
    targetStartDate,
    targetEndDate,
    isCumulative,
    weeklyViewToggle,
    enableHotelGroups,
    reportType,
    enableSecondaryHotelGroups,
  ]);

  // Generate utm visuals filter list
  useEffect(() => {
    if (reportType !== REPORT_TYPE.WEB_CLICK) return;
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    let newStartDate;
    let newEndDate;
    if (isCumulative) {
      newStartDate = targetDate?.startDate;
      newEndDate = targetDate?.endDate;
    } else {
      newStartDate = weeklyViewToggle ? targetStartDate : targetDate?.startDate;
      newEndDate = weeklyViewToggle ? targetEndDate : targetDate?.endDate;
    }
    const targetDateFilter = getTargetDateSchema(visualFilters, newStartDate, newEndDate);
    //
    let hotelIdFilter = [];
    if (enableHotelGroups) {
      const hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode);
      hotelIdFilter = getHotelIdSchema(hotelListIds, visualFilters);
    } else {
      hotelIdFilter = getHotelIdSchema(hotelId?.hotelCode, visualFilters);
    }
    const paceHorizonFilter = getPaceHorizonSchema(visualFilters, paceHorizon);
    const webClickStepsFilter = getWebClicksStepsSchema(visualFilters, webClickStep);
    const utmBreakdownFilter = getUtmBreakdownSchema(visualFilters, utmBreakdown);
    const UtmFocusOnFilter = getUtmFocusOnSchema(visualFilters, utmFocusOn);
    //
    if (
      bookingDateFilter &&
      targetDateFilter &&
      hotelIdFilter &&
      webClickStepsFilter &&
      utmBreakdownFilter &&
      UtmFocusOnFilter &&
      paceHorizonFilter
    ) {
      const filters = [
        bookingDateFilter,
        targetDateFilter,
        paceHorizonFilter,
        hotelIdFilter,
        webClickStepsFilter,
        utmBreakdownFilter,
        UtmFocusOnFilter,
      ];

      if (JSON.stringify(filters) !== JSON.stringify(utmVisualFilterList)) {
        //
        writeLog('UTM Filter', filters);
        dispatch(webClickActions.setUtmFilterList(filters));
      }
    }
  }, [
    bookingDate,
    visualFilters,
    targetDate,
    hotelId,
    paceHorizon,
    endDate,
    startDate,
    webClickStep,
    targetStartDate,
    targetEndDate,
    isCumulative,
    weeklyViewToggle,
    utmFocusOn,
    utmBreakdown,
    enableHotelGroups,
    reportType,
  ]);

  // Triggers when filter values dependencies changes
  useEffect(() => {
    if (!bookingDate && !targetDate && !hotelId && !paceHorizon && !dateComparison) return;
    // Generate web clicks filter url
    const webClickUrl = generateWebClicksShareUrl(
      pmsSyncToggle,
      hotelId,
      targetDate,
      dateComparison,
      vOTB,
      visualFilters,
      paceHorizon,
      startDate,
      endDate,
      customDate,
      weeklyViewToggle,
      isCumulative,
      normalizedViewToggle,
      webClickStep,
      scalingFactor,
      hotelFocusOn,
      focusOn,
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      enableHotelGroups,
      hotelId,
      enableSecondaryHotelGroups,
      compareWindowCollapseToggle,
      utmFocusOn,
      utmBreakdown,
      timeTrendIsDisplay,
      timePaceIsDisplay,
      segmentTrendIsDisplay,
      segmentPaceIsDisplay,
      hotelTrendIsDisplay,
      hotelPaceIsDisplay,
      utmTrendIsDisplay,
      utmPaceIsDisplay,
      localCurrency,
      focusOnList
    );
    //
    dispatch(webClickActions.setWebClickUrl(webClickUrl));
  }, [
    pmsSyncToggle,
    bookingDate,
    visualFilters,
    alignmentToggle,
    targetDate,
    hotelId,
    paceHorizon,
    symmetricComparisonToggle,
    vOTB,
    weeklyViewToggle,
    isCumulative,
    normalizedViewToggle,
    focusOn,
    hotelFocusOn,
    scalingFactor,
    webClickStep,
    startDate,
    endDate,
    customDate,
    enableHotelGroups,
    enableSecondaryHotelGroups,
    utmFocusOn,
    utmBreakdown,
    timeTrendIsDisplay,
    timePaceIsDisplay,
    segmentTrendIsDisplay,
    segmentPaceIsDisplay,
    hotelTrendIsDisplay,
    hotelPaceIsDisplay,
    utmTrendIsDisplay,
    utmPaceIsDisplay,
    compareWindowCollapseToggle,
    localCurrency,
    focusOnList,
  ]);
};
//
export default useWebClickView;
